/* DC Wave Field: a 3D particle wave plane, ported from JJ's Framer "Scroll Wave Field"
   (Originkit) to plain WebGL1, no React, no framer-motion. Component defaults kept:
   density 145, dot 2, scatter 108, camera height 50%, wave 250 / 200 / 2070, tilt 12,
   cursor lift 45 radius 25, spring gate on hover. The field streams endlessly toward the
   camera; the cursor is ray-cast onto the ground and lifts it with a gaussian bump.

   Usage: <canvas data-dc-wave data-colors='["#5A4AE0","#F2D98A"]'> inside a section;
   the section is the pointer host. Mounted lazily, paused off screen. */
(() => {
  if (window.__dcWave) return;
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const FIELD_W = 3600, FIELD_D = 7000, CAM_Z0 = 700, FOV = 60, DPR_CAP = 1.5, TAU = Math.PI * 2, MAX_COLORS = 8;
  const DEFAULT_COLORS = ['#5A4AE0', '#F2D98A'];
  const WAVE_DIR_X = 0, WAVE_DIR_Z = -1;
  const CAM_Y_FULL = 550, CAM_REF_AREA = 1200 * 800, CAM_SCALE_MIN = 0.5, CAM_SCALE_MAX = 2.5, CURSOR_FOLLOW = 7;
  const FLOW_PER_WAVE = 260 / 160, GLOW_PER_LIFT = 100 / 45;

  const VERT = `
precision highp float;
attribute vec2 aGrid; attribute vec2 aSeed;
uniform vec2 uRes; uniform float uFocal; uniform float uTime; uniform float uAmp; uniform float uScatter; uniform float uFreq;
uniform vec2 uDir; uniform float uFlow; uniform float uDepth; uniform float uCamY; uniform float uCamZ; uniform float uPitch; uniform float uRoll;
uniform float uDot; uniform float uColorCount; uniform vec2 uJit; uniform vec3 uColors[8]; uniform vec3 uCursor; uniform float uCurR; uniform float uCurS; uniform float uHover;
varying vec3 vCol; varying float vA; varying float vHot;
vec3 pickColor(float sel) { float idx = floor(sel * uColorCount); vec3 c = uColors[0]; for (int i = 1; i < 8; i++) { if (float(i) >= uColorCount) break; if (float(i) == idx) c = uColors[i]; } return c; }
float surf(vec2 q) { return sin(q.x) * 0.55 + sin(q.x * 0.55 + q.y * 1.15) * 0.30 + sin(q.y * 0.75) * 0.22; }
void main() {
  vec2 w = aGrid + (aSeed - 0.5) * uJit;
  w.y = uCamZ + mod(w.y - uFlow - uCamZ, uDepth);
  float h3 = fract(sin(dot(aSeed, vec2(91.37, 47.13))) * 12345.678);
  float h = surf(w * uFreq - uDir * uTime) * uAmp + (h3 - 0.5) * uScatter;
  float cd = length(w - uCursor.xy);
  float g = exp(-(cd * cd) / (uCurR * uCurR)) * uCursor.z;
  h += g * uCurS;
  float g2 = g * g; g2 = g2 * g2; g2 = g2 * g2;
  vec3 p = vec3(w.x, h - uCamY, w.y - uCamZ);
  float c = cos(uPitch); float s = sin(uPitch);
  float ry = p.y * c + p.z * s; float rz = -p.y * s + p.z * c;
  if (rz < 40.0) { gl_Position = vec4(2.0, 2.0, 0.0, 1.0); gl_PointSize = 0.0; vCol = uColors[0]; vA = 0.0; vHot = 0.0; return; }
  float cr = cos(uRoll); float sr = sin(uRoll);
  float rx = p.x * cr - ry * sr; float ryr = p.x * sr + ry * cr;
  float sx = rx * uFocal / rz; float sy = ryr * uFocal / rz;
  gl_Position = vec4(sx / (uRes.x * 0.5), sy / (uRes.y * 0.5), 0.0, 1.0);
  float rad = max(uDot * uFocal / rz, 0.55);
  gl_PointSize = clamp(rad * 2.0 * (1.0 + g2 * uHover * 0.20), 1.0, 220.0);
  float bri = 0.28 + h3 * 0.72;
  vec2 bq = w * vec2(0.0040, 0.0032) - uDir * uTime * 0.30;
  float band = sin(bq.x) + sin(bq.y);
  float sel = fract((band + 2.0) * 0.25 + (aSeed.y - 0.5) * 0.55);
  vCol = pickColor(sel);
  float lum = dot(vCol, vec3(0.299, 0.587, 0.114));
  vHot = (0.25 + 0.75 * lum) * bri * bri * 0.7 + g2 * uHover * 0.55;
  float fog = (1.0 - smoothstep(2800.0, 6400.0, rz)) * smoothstep(70.0, 240.0, rz);
  vA = bri * fog * (1.0 + g2 * uHover * 0.55);
}`;
  const FRAG = `
precision highp float;
varying vec3 vCol; varying float vA; varying float vHot;
void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0;
  if (d > 1.0) discard;
  float a = (1.0 - smoothstep(0.90, 1.0, d)) * vA;
  vec3 col = vCol + vec3(1.0) * pow(1.0 - d, 10.0) * vHot * 0.9;
  gl_FragColor = vec4(col * a, a);
}`;

  const parseColor = (input) => {
    if (!input) return [0, 0, 0];
    const s = String(input).trim();
    const fn = s.match(/rgba?\(([^)]+)\)/i);
    if (fn) { const p = fn[1].split(',').map((v) => parseFloat(v.trim())); return [(p[0] || 0) / 255, (p[1] || 0) / 255, (p[2] || 0) / 255]; }
    let h = s.replace('#', '');
    if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('');
    h = h.padEnd(6, '0');
    return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
  };
  const mulberry32 = (a) => function () { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const compile = (gl, type, src) => { const sh = gl.createShader(type); gl.shaderSource(sh, src); gl.compileShader(sh); if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) || 'shader'); return sh; };

  function mount(canvas) {
    const host = canvas.closest('section') || canvas.parentElement;
    const phone = matchMedia('(max-width: 767px)').matches;
    let colors = DEFAULT_COLORS;
    try { const c = JSON.parse(canvas.dataset.colors || 'null'); if (Array.isArray(c) && c.length) colors = c.slice(0, MAX_COLORS); } catch (e) {}
    const L = {
      density: parseFloat(canvas.dataset.density) || (phone ? 100 : 145), dotSize: parseFloat(canvas.dataset.dot) || 2, scatter: 108,
      waveHeight: 200, waveLength: 2070, waveSpeed: 250, flowSpeed: 250 * FLOW_PER_WAVE,
      tiltStart: 12, rollStart: 0, cameraHeight: parseFloat(canvas.dataset.camera) || 50,
      cursorRadius: 25, cursorLift: 45, hoverGlow: Math.min(400, 45 * GLOW_PER_LIFT)
    };
    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: true, depth: false });
    if (!gl) return null;
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    const aGrid = gl.getAttribLocation(prog, 'aGrid'), aSeed = gl.getAttribLocation(prog, 'aSeed');
    const U = (n) => gl.getUniformLocation(prog, n);
    const u = { res: U('uRes'), focal: U('uFocal'), time: U('uTime'), amp: U('uAmp'), scatter: U('uScatter'), freq: U('uFreq'), dir: U('uDir'), flow: U('uFlow'), depth: U('uDepth'), camY: U('uCamY'), camZ: U('uCamZ'), pitch: U('uPitch'), roll: U('uRoll'), dot: U('uDot'), colorCount: U('uColorCount'), jit: U('uJit'), colors: U('uColors[0]'), cursor: U('uCursor'), curR: U('uCurR'), curS: U('uCurS'), hover: U('uHover') };
    const gridBuf = gl.createBuffer(), seedBuf = gl.createBuffer();
    const palBuf = new Float32Array(MAX_COLORS * 3);
    let count = 0, spacingX = 1, spacingZ = 1;
    const buildGrid = (d) => {
      const cols = Math.max(8, Math.round(d)), rows = Math.max(8, Math.round(d * 2));
      count = cols * rows; spacingX = FIELD_W / (cols - 1); spacingZ = FIELD_D / (rows - 1);
      const grid = new Float32Array(count * 2), seed = new Float32Array(count * 2), rnd = mulberry32(0x5eed);
      let i = 0;
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) { grid[i * 2] = -FIELD_W / 2 + (c + 0.5) * spacingX; grid[i * 2 + 1] = r * spacingZ; seed[i * 2] = rnd(); seed[i * 2 + 1] = rnd(); i++; }
      gl.bindBuffer(gl.ARRAY_BUFFER, gridBuf); gl.bufferData(gl.ARRAY_BUFFER, grid, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, seedBuf); gl.bufferData(gl.ARRAY_BUFFER, seed, gl.STATIC_DRAW);
    };
    buildGrid(L.density);
    gl.disable(gl.DEPTH_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE);
    for (let i = 0; i < colors.length; i++) { const c = parseColor(colors[i]); palBuf[i * 3] = c[0]; palBuf[i * 3 + 1] = c[1]; palBuf[i * 3 + 2] = c[2]; }

    let cssW = 0, cssH = 0, dpr = 1, areaScale = 1;
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      cssW = canvas.clientWidth || host.clientWidth || 0; cssH = canvas.clientHeight || host.clientHeight || 0;
      areaScale = cssW > 0 && cssH > 0 ? Math.min(CAM_SCALE_MAX, Math.max(CAM_SCALE_MIN, Math.sqrt((cssW * cssH) / CAM_REF_AREA))) : 1;
      const w = Math.max(1, Math.round(cssW * dpr)), h = Math.max(1, Math.round(cssH * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, w, h);
    };
    resize();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(canvas);

    /* Pointer and the two gates: exponential approaches stand in for the spring. */
    const p = { x: 0, y: 0, sx: 0, sy: 0, active: 0, target: 0, press: 0, pressTarget: 0 };
    const onMove = (e) => { const r = host.getBoundingClientRect(); p.x = e.clientX - r.left; p.y = e.clientY - r.top; p.target = 1; };
    const onLeave = () => { p.target = 0; p.pressTarget = 0; };
    const onDown = () => { p.pressTarget = 1; };
    const onUp = () => { p.pressTarget = 0; };
    host.addEventListener('pointermove', onMove, { passive: true });
    host.addEventListener('pointerleave', onLeave, { passive: true });
    host.addEventListener('pointerdown', onDown, { passive: true });
    host.addEventListener('pointercancel', onUp, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });

    let raf = 0, last = performance.now(), phase = 0, flow = 0, hitX = 0, hitZ = -1e6, alive = true, visible = false;
    const groundHit = (mx, my, wDev, hDev, focal, pitch, roll, camY, camZ) => {
      const px = mx * dpr - wDev / 2, py = -(my * dpr - hDev / 2);
      const cr = Math.cos(roll), sr = Math.sin(roll);
      const sx = px * cr + py * sr, sy = -px * sr + py * cr;
      const dx = sx / focal, dy = sy / focal, c = Math.cos(pitch), s = Math.sin(pitch);
      const wy = dy * c - s, wz = dy * s + c;
      if (wy > -1e-4) return null;
      const t = -camY / wy;
      return { x: dx * t, z: camZ + wz * t };
    };
    const frame = (now) => {
      raf = 0;
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      const dt = Math.min((now - last) / 1000, 0.05); last = now;
      if (!visible) return;
      if (cssW <= 0 || cssH <= 0) { resize(); if (cssW <= 0 || cssH <= 0) return; }
      const ka = 1 - Math.exp(-dt * 6), kp = 1 - Math.exp(-dt * 7);
      p.active += (p.target - p.active) * ka; p.press += (p.pressTarget - p.press) * kp;
      const speedMul = 1 + p.press;
      if (p.active < 0.002) { p.sx = p.x; p.sy = p.y; } else { const k = 1 - Math.exp(-dt * CURSOR_FOLLOW); p.sx += (p.x - p.sx) * k; p.sy += (p.y - p.sy) * k; }
      phase += dt * (L.waveSpeed / 100) * speedMul;
      flow = (flow + dt * L.flowSpeed * speedMul) % FIELD_D;
      const pitch = (L.tiltStart * Math.PI) / 180, roll = (-L.rollStart * Math.PI) / 180;
      const camY = (Math.min(100, Math.max(0, L.cameraHeight)) / 100) * CAM_Y_FULL * areaScale, camZ = CAM_Z0;
      const wDev = canvas.width, hDev = canvas.height, focal = hDev / (2 * Math.tan(((FOV / 2) * Math.PI) / 180));
      if (p.active <= 0.001) { hitX = 0; hitZ = -1e6; }
      else { const hit = groundHit(p.sx, p.sy, wDev, hDev, focal, pitch, roll, camY, camZ); if (hit) { hitX = hit.x; hitZ = hit.z; } else if (hitZ === -1e6) { hitX = 0; hitZ = FIELD_D; } }
      gl.uniform2f(u.res, wDev, hDev); gl.uniform1f(u.focal, focal); gl.uniform1f(u.time, phase);
      gl.uniform1f(u.amp, L.waveHeight); gl.uniform1f(u.scatter, L.scatter); gl.uniform1f(u.freq, TAU / Math.max(50, L.waveLength));
      gl.uniform2f(u.dir, WAVE_DIR_X, WAVE_DIR_Z); gl.uniform1f(u.flow, flow); gl.uniform1f(u.depth, FIELD_D);
      gl.uniform1f(u.camY, camY); gl.uniform1f(u.camZ, camZ); gl.uniform1f(u.pitch, pitch); gl.uniform1f(u.roll, roll);
      gl.uniform1f(u.dot, L.dotSize); gl.uniform1f(u.colorCount, colors.length); gl.uniform2f(u.jit, spacingX * 0.25, spacingZ * 0.7);
      gl.uniform3fv(u.colors, palBuf); gl.uniform3f(u.cursor, hitX, hitZ, p.active);
      gl.uniform1f(u.curR, Math.max(1, (L.cursorRadius / 100) * (FIELD_W / 2))); gl.uniform1f(u.curS, L.cursorLift); gl.uniform1f(u.hover, L.hoverGlow / 100);
      gl.bindBuffer(gl.ARRAY_BUFFER, gridBuf); gl.enableVertexAttribArray(aGrid); gl.vertexAttribPointer(aGrid, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, seedBuf); gl.enableVertexAttribArray(aSeed); gl.vertexAttribPointer(aSeed, 2, gl.FLOAT, false, 0, 0);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.POINTS, 0, count);
    };
    const wake = () => { if (!raf && alive) { last = performance.now(); raf = requestAnimationFrame(frame); } };
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => { visible = es.some((e) => e.isIntersecting); if (visible) wake(); }, { threshold: 0.01 }) : null;
    if (io) io.observe(canvas); else { visible = true; }
    wake();
    canvas.classList.add('is-live');
    return {
      destroy() {
        alive = false; if (raf) cancelAnimationFrame(raf); raf = 0;
        try { ro && ro.disconnect(); } catch (e) {}
        try { io && io.disconnect(); } catch (e) {}
        host.removeEventListener('pointermove', onMove); host.removeEventListener('pointerleave', onLeave); host.removeEventListener('pointerdown', onDown); host.removeEventListener('pointercancel', onUp);
        window.removeEventListener('pointerup', onUp);
        canvas.classList.remove('is-live');
      }
    };
  }

  const init = (scope) => {
    if (REDUCED) return;
    (scope || document).querySelectorAll('canvas[data-dc-wave]').forEach((c) => {
      if (c.__dcWave || c.__dcWaveLazy) return;
      const go = () => { c.__dcWaveLazy = null; try { c.__dcWave = mount(c); } catch (e) { c.__dcWave = null; } };
      if (!('IntersectionObserver' in window)) { go(); return; }
      const lz = new IntersectionObserver((es, obs) => { if (es.some((e) => e.isIntersecting)) { obs.disconnect(); go(); } }, { rootMargin: '300px 0px' });
      lz.observe(c); c.__dcWaveLazy = lz;
    });
  };
  const destroy = (scope) => (scope || document).querySelectorAll('canvas[data-dc-wave]').forEach((c) => { if (c.__dcWave) { try { c.__dcWave.destroy(); } catch (e) {} c.__dcWave = null; } if (c.__dcWaveLazy) { try { c.__dcWaveLazy.disconnect(); } catch (e) {} c.__dcWaveLazy = null; } });
  window.__dcWave = { init, destroy, mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => init(e.target));
    document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  }
})();
